
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Briefcase, 
  Star, 
  Edit,
  Settings,
  Shield,
  Bell
} from "lucide-react";

export const ProfileSection = () => {
  const { user } = useAuth();

  const { data: profile, isLoading } = useQuery({
    queryKey: ["mobile-profile"],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Profil Professionnel</h2>
        <Button size="sm" variant="outline">
          <Edit className="h-4 w-4 mr-2" />
          Modifier
        </Button>
      </div>

      {/* Profile Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              {profile?.profile_photo ? (
                <img 
                  src={profile.profile_photo} 
                  alt={profile.full_name}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <User className="h-8 w-8 text-blue-600" />
              )}
            </div>
            
            <div className="flex-1">
              <h3 className="text-lg font-semibold">{profile?.full_name || 'Nom non renseigné'}</h3>
              <p className="text-gray-600 mb-2">{profile?.type === 'professional' ? 'Professionnel de santé' : 'Utilisateur'}</p>
              
              <div className="flex items-center gap-4 text-sm">
                {profile?.rating && (
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 text-yellow-500 fill-current" />
                    <span className="font-medium">{profile.rating}/5</span>
                    {profile.total_reviews && (
                      <span className="text-gray-500">({profile.total_reviews} avis)</span>
                    )}
                  </div>
                )}
                {profile?.verified && (
                  <Badge variant="outline" className="text-xs">
                    <Shield className="h-3 w-3 mr-1" />
                    Vérifié
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle>Informations de Contact</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Mail className="h-4 w-4 text-gray-500" />
            <span className="text-sm">{profile?.email || 'Email non renseigné'}</span>
          </div>
          
          {profile?.phone_number && (
            <div className="flex items-center gap-3">
              <Phone className="h-4 w-4 text-gray-500" />
              <span className="text-sm">{profile.phone_number}</span>
            </div>
          )}
          
          {profile?.practice_address && (
            <div className="flex items-center gap-3">
              <MapPin className="h-4 w-4 text-gray-500" />
              <span className="text-sm">{profile.practice_address}</span>
            </div>
          )}
          
          {profile?.company_name && (
            <div className="flex items-center gap-3">
              <Briefcase className="h-4 w-4 text-gray-500" />
              <span className="text-sm">{profile.company_name}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Professional Information */}
      {profile?.specialties && profile.specialties.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Spécialités</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {profile.specialties.map((specialty, index) => (
                <Badge key={index} variant="secondary">
                  {specialty}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* About */}
      {profile?.about_me && (
        <Card>
          <CardHeader>
            <CardTitle>À propos</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">{profile.about_me}</p>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Actions Rapides</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3">
            <Button variant="outline" className="justify-start">
              <Settings className="h-4 w-4 mr-2" />
              Paramètres du compte
            </Button>
            <Button variant="outline" className="justify-start">
              <Bell className="h-4 w-4 mr-2" />
              Notifications
            </Button>
            <Button variant="outline" className="justify-start">
              <Shield className="h-4 w-4 mr-2" />
              Sécurité
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Statistiques</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{profile?.patient_files_count || 0}</div>
              <div className="text-xs text-gray-500">Dossiers patients</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{profile?.staff_count || 0}</div>
              <div className="text-xs text-gray-500">Membres d'équipe</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
